import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  Typography,
  TextField,
  Grid,
  Snackbar,
  IconButton,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { parse, isAfter, isBefore, isEqual } from "date-fns";
import axios from "axios";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { toUTC, fromUTC } from "../GeneralUtil/timezoneUtil";

interface TimeSlot {
  id?: number;
  start_time: string;
  end_time: string;
  max: number | undefined;
}

interface AvailabilityManagerProps {
  accountId?: number | null;
  baId?: number | null;
}

const daysOfWeek = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

interface ExcludedDate {
  id: number;
  exclusion_date: string;
}

const AvailabilityManager: React.FC<AvailabilityManagerProps> = ({
  accountId,
  baId,
}) => {
  const isaccount = !!accountId; // Determine if the user is a account
  const [selectedDay, setSelectedDay] = useState("Monday");
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [newSlot, setNewSlot] = useState<TimeSlot>({
    start_time: "",
    end_time: "",
    max: isaccount ? 1 : undefined, // Set max only for accounts
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [excludedDates, setExcludedDates] = useState<ExcludedDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>("");

  useEffect(() => {
    fetchAvailability(); // Combined function to fetch availability
    fetchExcludedDates(); // Combined function to fetch excluded dates
  }, [selectedDay, accountId, baId]);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(
        isaccount
          ? `${
              import.meta.env.VITE_API_URL
            }/availability/account_availability/${accountId}`
          : `${import.meta.env.VITE_API_URL}/availability/ba_availability`,
        {
          params: {
            user_id: isaccount ? accountId : baId,
            day_of_week: selectedDay,
          },
        }
      );

      // Convert fetched time slots from UTC to local time
      const localTimeSlots = response.data.map((slot: TimeSlot) => {
        const startTime = slot.start_time;
        const endTime = slot.end_time;
        // Check if start_time and end_time are valid
        if (!startTime || !endTime) {
          console.warn("Invalid time values:", { startTime, endTime });
          return {
            ...slot,
            start_time: "Invalid",
            end_time: "Invalid",
          };
        }

        const localStartTime = fromUTC(startTime);
        const localEndTime = fromUTC(endTime);
        return {
          ...slot,
          start_time: localStartTime || "Invalid",
          end_time: localEndTime || "Invalid",
        };
      });

      setTimeSlots(localTimeSlots);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const fetchExcludedDates = async () => {
    try {
      const response = await axios.get(
        isaccount
          ? `${
              import.meta.env.VITE_API_URL
            }/availability/account_exclusions/${accountId}`
          : `${import.meta.env.VITE_API_URL}/availability/ba_exclusions/${baId}` // Adjusted API endpoint for BA
      );
      setExcludedDates(response.data);
    } catch (error) {
      console.error("Error fetching excluded dates:", error);
    }
  };

  const handleDayChange = (day: string) => {
    setSelectedDay(day);
  };

  const handleNewSlotChange = (
    field: "start_time" | "end_time" | "max",
    value: string | number
  ) => {
    setNewSlot((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const addNewTimeSlot = async () => {
    // Validate the new slot
    if (!newSlot.start_time || !newSlot.end_time) {
      setSnackbarMessage("Please complete both start and end times.");
      setSnackbarOpen(true);
      return;
    }

    // Check if start and end times match
    if (newSlot.start_time === newSlot.end_time) {
      setSnackbarMessage("Brands will need a little more time than this.");
      setSnackbarOpen(true);
      return;
    }

    // Check if end time is before start time
    const fromTime = parse(newSlot.start_time, "HH:mm", new Date());
    const toTime = parse(newSlot.end_time, "HH:mm", new Date());
    if (isBefore(toTime, fromTime)) {
      setSnackbarMessage(
        "Promoraven is not a time machine. Adjust your time slots."
      );
      setSnackbarOpen(true);
      return;
    }

    if (isOverlapping(newSlot, -1)) {
      setSnackbarMessage("Time slots cannot overlap.");
      setSnackbarOpen(true);
      return;
    }

    try {
      const apiEndpoint = isaccount
        ? `${import.meta.env.VITE_API_URL}/availability/account_availability`
        : `${import.meta.env.VITE_API_URL}/availability/ba_availability`;

      // Convert local time to UTC before sending
      const utcStartTime = toUTC(newSlot.start_time); // Convert to UTC
      const utcEndTime = toUTC(newSlot.end_time); // Convert to UTC

      await axios.post(apiEndpoint, {
        account_id: isaccount ? accountId : undefined,
        user_id: isaccount ? undefined : baId,
        day_of_week: selectedDay,
        start_time: utcStartTime,
        end_time: utcEndTime,
        ...(isaccount && { max: newSlot.max }),
      });

      // Refresh the time slots
      await fetchAvailability();

      // Clear the new slot form
      setNewSlot({
        start_time: "",
        end_time: "",
        max: isaccount ? 1 : undefined,
      });

      setSnackbarMessage("Time slot added successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error adding time slot:", error);
      setSnackbarMessage("Error adding time slot. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const removeTimeSlot = async (id: number) => {
    try {
      const apiEndpoint = isaccount
        ? `${
            import.meta.env.VITE_API_URL
          }/availability/account_availability/${id}`
        : `${import.meta.env.VITE_API_URL}/availability/ba_availability/${id}`; // Adjusted API endpoint for BA

      await axios.delete(apiEndpoint);
      await fetchAvailability();
      setSnackbarMessage("Time slot removed successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing time slot:", error);
      setSnackbarMessage("Error removing time slot. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const isOverlapping = (newSlot: TimeSlot, index: number): boolean => {
    const newStart = parse(newSlot.start_time, "HH:mm", new Date());
    const newEnd = parse(newSlot.end_time, "HH:mm", new Date());

    return timeSlots.some((slot, i) => {
      if (i === index) return false; // Skip comparing with itself
      const slotStart = parse(slot.start_time, "HH:mm", new Date());
      const slotEnd = parse(slot.end_time, "HH:mm", new Date());

      return (
        (isAfter(newStart, slotStart) && isBefore(newStart, slotEnd)) ||
        (isAfter(newEnd, slotStart) && isBefore(newEnd, slotEnd)) ||
        (isBefore(newStart, slotStart) && isAfter(newEnd, slotEnd)) ||
        isEqual(newStart, slotStart) ||
        isEqual(newEnd, slotEnd)
      );
    });
  };

  const addExcludedDate = async () => {
    if (!selectedDate) return;

    try {
      await axios.post(
        isaccount
          ? `${import.meta.env.VITE_API_URL}/availability/account_exclusions`
          : `${import.meta.env.VITE_API_URL}/availability/ba_exclusions`, // Adjusted API endpoint for BA
        {
          account_id: isaccount ? accountId : undefined, // Use accountId for accounts
          user_id: isaccount ? undefined : baId, // Use baId for BAs
          exclusion_date: selectedDate,
        }
      );
      await fetchExcludedDates(); // Refresh excluded dates
      setSelectedDate(""); // Clear selected date
      setSnackbarMessage("Date excluded successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error adding excluded date:", error);
      setSnackbarMessage("Error adding excluded date. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const removeExcludedDate = async (id: number) => {
    try {
      await axios.delete(
        isaccount
          ? `${
              import.meta.env.VITE_API_URL
            }/availability/account_exclusions/${id}`
          : `${import.meta.env.VITE_API_URL}/availability/ba_exclusions/${id}` // Adjusted API endpoint for BA
      );
      await fetchExcludedDates(); // Refresh excluded dates
      setSnackbarMessage("Date exclusion removed successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error removing excluded date:", error);
      setSnackbarMessage("Error removing excluded date. Please try again.");
      setSnackbarOpen(true);
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  // Sort time slots chronologically
  const sortedTimeSlots = [...timeSlots].sort((a, b) =>
    a.start_time.localeCompare(b.start_time)
  );

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Availability Manager
      </Typography>

      {/* Time slots section */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} sm="auto">
            <Typography sx={{ fontWeight: 500 }}>Select Day:</Typography>
          </Grid>
          <Grid item xs={12} sm>
            <Select
              value={selectedDay}
              onChange={(e) => handleDayChange(e.target.value as string)}
              sx={{ minWidth: 200 }}
            >
              {daysOfWeek.map((day) => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mb: 3 }}>
          Time slots for {selectedDay}:
        </Typography>

        {/* Existing time slots */}
        {sortedTimeSlots.map((slot, index) => (
          <Grid
            container
            spacing={2}
            key={slot.id}
            sx={{
              mb: 2,
              alignItems: "center",
              backgroundColor: "background.paper",
              p: 1,
              borderRadius: 1,
            }}
          >
            <Grid item xs={2}>
              <Typography sx={{ fontWeight: 500 }}>
                Slot {index + 1}:
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <TextField
                disabled
                label="From"
                type="time"
                value={slot.start_time}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                disabled
                label="To"
                type="time"
                value={slot.end_time}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1800 }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={2}>
              {isaccount && (
                <TextField
                  disabled
                  select
                  label="Max #"
                  value={slot.max}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={() => removeTimeSlot(slot.id!)}
                color="error"
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: "error.lighter",
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        {/* New slot entry row */}
        <Grid
          container
          spacing={2}
          sx={{
            mb: 2,
            alignItems: "center",
            backgroundColor: "background.paper",
            p: 1,
            borderRadius: 1,
            borderStyle: "dashed",
            borderWidth: 1,
            borderColor: "divider",
          }}
        >
          <Grid item xs={2}>
            <Typography sx={{ fontWeight: 500, color: "text.secondary" }}>
              Add New Slot:
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="From"
              type="time"
              value={newSlot.start_time}
              onChange={(e) =>
                handleNewSlotChange("start_time", e.target.value)
              }
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1800 }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              label="To"
              type="time"
              value={newSlot.end_time}
              onChange={(e) => handleNewSlotChange("end_time", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 1800 }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={2}>
            {isaccount && (
              <TextField
                select
                label="Max #"
                value={newSlot.max}
                onChange={(e) => handleNewSlotChange("max", e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Grid>
          <Grid item xs={2}>
            <IconButton
              onClick={addNewTimeSlot}
              color="primary"
              size="small"
              sx={{
                "&:hover": {
                  backgroundColor: "primary.lighter",
                },
              }}
            >
              <AddIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Paper>

      {/* Excluded dates section */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Excluded Dates
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date to Exclude"
                value={selectedDate ? dayjs(selectedDate) : null}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedDate(newValue.format("YYYY-MM-DD"));
                  }
                }}
                minDate={dayjs()} // This sets today as the minimum date
                sx={{ width: "100%" }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm="auto">
            <IconButton
              onClick={addExcludedDate}
              color="primary"
              sx={{
                "&:hover": {
                  backgroundColor: "primary.lighter",
                },
              }}
            >
              <AddIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {excludedDates
            .sort(
              (a, b) =>
                new Date(a.exclusion_date).getTime() -
                new Date(b.exclusion_date).getTime()
            )
            .map((date) => (
              <Chip
                key={date.id}
                label={formatDate(date.exclusion_date)}
                onDelete={() => removeExcludedDate(date.id)}
                sx={{ mb: 1 }}
              />
            ))}
        </Stack>
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Paper>
  );
};

export default AvailabilityManager;
